import { useEffect, useMemo, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CreateMenuNodePayload, MenuNode } from '@/client/types/menus';
import { useCMSListQuery } from '@/client/api/cms/cms.query';
import SearchableSelect from '@/client/components/admin/SearchableSelect';

export type MenuNodeFormState = {
  name: string;
  name_system: string;
  description: string;
  order_by: string;
  locale: string;
  linkType: 'none' | 'cmsoid' | 'ext_link' | 'etid';
  cmsoid: string;
  ext_link: string;
  etid: string;
  menid_parent: string;
  menid_root: string;
  active: boolean;
};

const emptyForm = (defaults?: Partial<MenuNodeFormState>): MenuNodeFormState => ({
  name: '',
  name_system: '',
  description: '',
  order_by: '',
  locale: 'en_GB',
  linkType: 'none',
  cmsoid: '',
  ext_link: '',
  etid: '',
  menid_parent: '',
  menid_root: '',
  active: true,
  ...defaults,
});

export const nodeToForm = (node: MenuNode): MenuNodeFormState => {
  let linkType: MenuNodeFormState['linkType'] = 'none';
  if (node.cmsoid != null) linkType = 'cmsoid';
  else if (node.ext_link) linkType = 'ext_link';
  else if (node.etid != null) linkType = 'etid';

  return emptyForm({
    name: node.name ?? '',
    name_system: node.name_system ?? '',
    description: node.description ?? '',
    order_by: node.order_by != null ? String(node.order_by) : '',
    locale: node.locale ?? 'en_GB',
    linkType,
    cmsoid: node.cmsoid != null ? String(node.cmsoid) : '',
    ext_link: node.ext_link ?? '',
    etid: node.etid != null ? String(node.etid) : '',
    menid_parent: node.menid_parent != null ? String(node.menid_parent) : '',
    menid_root: node.menid_root != null ? String(node.menid_root) : '',
    active: node.active !== 0 && node.active !== false,
  });
};

export const formToPayload = (form: MenuNodeFormState): CreateMenuNodePayload => {
  const payload: CreateMenuNodePayload = {
    name: form.name.trim(),
    active: form.active ? 1 : 0,
  };
  if (form.name_system.trim()) payload.name_system = form.name_system.trim();
  if (form.description.trim()) payload.description = form.description.trim();
  if (form.locale.trim()) payload.locale = form.locale.trim();
  if (form.order_by !== '') payload.order_by = Number(form.order_by);
  if (form.menid_parent !== '') payload.menid_parent = Number(form.menid_parent);
  if (form.menid_root !== '') payload.menid_root = Number(form.menid_root);

  // Deep-link precedence: set one, clear the others
  payload.cmsoid = null;
  payload.ext_link = null;
  payload.etid = null;
  if (form.linkType === 'cmsoid' && form.cmsoid !== '') {
    payload.cmsoid = Number(form.cmsoid);
  } else if (form.linkType === 'ext_link' && form.ext_link.trim()) {
    payload.ext_link = form.ext_link.trim();
  } else if (form.linkType === 'etid' && form.etid !== '') {
    payload.etid = Number(form.etid);
  }

  return payload;
};

interface MenuNodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  initial?: MenuNodeFormState;
  saving?: boolean;
  showParentFields?: boolean;
  onSubmit: (form: MenuNodeFormState) => void | Promise<void>;
}

export const MenuNodeDialog = ({
  open,
  onOpenChange,
  title,
  initial,
  saving,
  showParentFields = true,
  onSubmit,
}: MenuNodeDialogProps) => {
  const [form, setForm] = useState<MenuNodeFormState>(initial ?? emptyForm());
  const [cmsSearch, setCmsSearch] = useState('');

  useEffect(() => {
    if (open) {
      setForm(initial ?? emptyForm());
      setCmsSearch('');
    }
  }, [open, initial]);

  const cmsQuery = useCMSListQuery(
    1,
    {
      limit: 40,
      q: cmsSearch.trim() || undefined,
    },
    { enabled: open && form.linkType === 'cmsoid' },
  );
  const cmsOptions = useMemo(() => {
    const items = cmsQuery.data?.items ?? [];
    const opts = items.map((p) => ({
      value: String(p.cmsoid),
      label: `${p.title || '(untitled)'} · ${p.slug} (#${p.cmsoid})`,
    }));
    // Keep current selection visible even if not in this page of results
    if (form.cmsoid && !opts.some((o) => o.value === form.cmsoid)) {
      opts.unshift({
        value: form.cmsoid,
        label: `CMS #${form.cmsoid}`,
      });
    }
    return opts;
  }, [cmsQuery.data?.items, form.cmsoid]);

  const set = <K extends keyof MenuNodeFormState>(key: K, value: MenuNodeFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="menu-name">Name</Label>
            <Input
              id="menu-name"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="About us"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="menu-system">System name</Label>
              <Input
                id="menu-system"
                value={form.name_system}
                onChange={(e) => set('name_system', e.target.value)}
                placeholder="footer_about"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="menu-order">Order</Label>
              <Input
                id="menu-order"
                type="number"
                value={form.order_by}
                onChange={(e) => set('order_by', e.target.value)}
                placeholder="10"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="menu-desc">Description</Label>
            <Input
              id="menu-desc"
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
            />
          </div>

          {showParentFields && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="menu-root">Root menid</Label>
                <Input
                  id="menu-root"
                  type="number"
                  value={form.menid_root}
                  onChange={(e) => set('menid_root', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="menu-parent">Parent menid</Label>
                <Input
                  id="menu-parent"
                  type="number"
                  value={form.menid_parent}
                  onChange={(e) => set('menid_parent', e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="menu-locale">Locale</Label>
            <Input
              id="menu-locale"
              value={form.locale}
              onChange={(e) => set('locale', e.target.value)}
              placeholder="en_GB"
            />
          </div>

          <div className="space-y-2 border-t pt-3">
            <Label>Deep link</Label>
            <p className="text-xs text-gray-500">
              Set one: CMS page, external URL, or entity-type browse. Leave none for a label-only
              node.
            </p>
            <select
              className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
              value={form.linkType}
              onChange={(e) => set('linkType', e.target.value as MenuNodeFormState['linkType'])}
            >
              <option value="none">None (label only)</option>
              <option value="cmsoid">CMS page (cmsoid)</option>
              <option value="ext_link">External / path URL</option>
              <option value="etid">Entity type (etid)</option>
            </select>

            {form.linkType === 'cmsoid' && (
              <SearchableSelect
                options={cmsOptions}
                value={form.cmsoid}
                onChange={(v) => set('cmsoid', v)}
                onSearchChange={setCmsSearch}
                placeholder="Search CMS pages by title or slug…"
              />
            )}
            {form.linkType === 'ext_link' && (
              <Input
                placeholder="/careers or https://…"
                value={form.ext_link}
                onChange={(e) => set('ext_link', e.target.value)}
              />
            )}
            {form.linkType === 'etid' && (
              <Input
                type="number"
                placeholder="etid"
                value={form.etid}
                onChange={(e) => set('etid', e.target.value)}
              />
            )}
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => set('active', e.target.checked)}
              className="rounded border-gray-300"
            />
            Active
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              disabled={saving || !form.name.trim()}
              onClick={() => onSubmit(form)}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { emptyForm };
